import React from 'react'
import ReactDOM from 'react-dom/client'
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import './index.css'
import App from './App'
import { initStatusBar } from './utils/nativeStatusBar'
import { installSessionGuard } from './utils/sessionGuard'
import { hydrateSessionFromNative } from './utils/nativeSessionStore'
import { initSafeAreaInsets } from './utils/safeAreaInsets'

// Keep the OS status bar (clock/battery) visible on the native shell.
initStatusBar()

// Publish the real device insets to CSS before first paint, so no screen ever
// lays a button under the Android navigation bar. Must run before render.
initSafeAreaInsets()

// Auto-logout when the token is rejected (expired or superseded by a login on
// another device). Installed before render so it covers every request.
installSessionGuard()

// On native, WKWebView/Chromium's localStorage can be evicted by the OS even
// though the account's 365-day session is still valid - restore it from
// Preferences (UserDefaults/SharedPreferences) first so the first render's
// isAuthenticated() check never sees a false "logged out". No-op on web and
// whenever localStorage already has a session. Runs before render, still under
// the native gold splash screen, so there's nothing to flash.
hydrateSessionFromNative().finally(() => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  )

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  )

  // The native gold splash (launchAutoHide:false) covers the whole remote load;
  // hide it now that our JS is running. index.html's #sc-splash - also gold - is
  // already painted underneath, so the hand-off stays gold with no flash. Wait one
  // frame so that web splash is definitely on screen before the native one lifts.
  if (Capacitor.isNativePlatform()) {
    requestAnimationFrame(() => SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {}))
  }
})

// Register service worker + auto-update (reload once when a new version takes over)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  });
  let hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) { hadController = true; return; }
    window.location.reload();
  });
  const checkUpdate = () => navigator.serviceWorker.getRegistration().then((r) => r?.update()).catch(() => {});
  window.addEventListener('focus', checkUpdate);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') checkUpdate(); });
}
